/**
 * SharingSettingsPage - Configuration du partage des ressources
 * Super-admin uniquement — configure le perimetre (org vs establishment) par type de ressource
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useViewport } from '../hooks/useViewport';
import { useHasPermission } from '../SupabaseWrapper';
import { ShieldIcon } from '../icons';

interface ResourceConfig {
  scope: 'organization' | 'establishment';
  label: string;
  description: string;
}

interface SharingConfig {
  [key: string]: ResourceConfig;
}

const API_BASE = window.location.origin;

// Icones par ressource
const RESOURCE_ICONS: Record<string, string> = {
  articles: '\u{1F4E6}',
  statuses: '\u{1F4CB}',
  profiles: '\u{1F464}'
};

// Avertissements par ressource quand on change de scope
const SCOPE_WARNINGS: Record<string, Record<string, string | null>> = {
  articles: {
    establishment: 'Les articles existants (partagés) ne seront plus visibles. Relancez une synchronisation IBM i par établissement.',
    organization: 'Les articles isolés par établissement ne seront plus visibles. Relancez une synchronisation IBM i globale.'
  },
  statuses: {
    organization: null,
    establishment: null
  },
  profiles: {
    organization: null,
    establishment: null
  }
};

interface SharingSettingsPageProps {
  refreshTrigger?: number;
}

function SharingSettingsPage({ refreshTrigger = 0 }: SharingSettingsPageProps) {
  const { isPhone } = useViewport();
  // RBAC : permissions
  const canView = useHasPermission('sharing.view');
  const canManage = useHasPermission('sharing.manage');

  const [config, setConfig] = useState<SharingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({
    show: false, type: 'success', message: ''
  });

  const getToken = () => localStorage.getItem('sso_token');

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 4000);
  };

  const fetchConfig = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/api/settings/sharing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setConfig(data.config);
      }
    } catch (err) {
      console.error('[SharingSettings] Erreur chargement:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Refetch sur changement d'onglet
  const isFirstMount = React.useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    fetchConfig();
  }, [refreshTrigger, fetchConfig]);

  async function handleScopeChange(resource: string, newScope: 'organization' | 'establishment') {
    if (!config) return;
    if (config[resource]?.scope === newScope) return;

    setUpdating(resource);
    const token = getToken();

    try {
      const res = await fetch(`${API_BASE}/api/settings/sharing/${resource}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ scope: newScope })
      });
      const data = await res.json();
      if (data.success) {
        setConfig(prev => prev ? {
          ...prev,
          [resource]: { ...prev[resource], scope: newScope }
        } : null);
        showToast('success', data.message || 'Configuration mise a jour');
      } else {
        showToast('error', data.error?.message || 'Erreur');
      }
    } catch (err) {
      console.error('[SharingSettings] Erreur update:', err);
      showToast('error', 'Erreur de connexion');
    } finally {
      setUpdating(null);
    }
  }

  if (!canView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16 }}>
        <ShieldIcon size={64} color="#ef4444" />
        <h2 style={{ color: '#1e293b', margin: 0 }}>Accès refusé</h2>
        <p style={{ color: '#64748b', margin: 0 }}>Vous n'avez pas les autorisations nécessaires pour accéder à cette page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <div style={{
          width: '32px', height: '32px',
          border: '3px solid #e2e8f0', borderTop: '3px solid #3b82f6',
          borderRadius: '50%', animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        Erreur de chargement de la configuration
      </div>
    );
  }

  const resources = Object.entries(config);

  return (
    <div style={{ position: 'relative' }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 1000,
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '12px 20px',
          backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#86efac' : '#fecaca'}`,
          borderRadius: '10px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease'
        }}>
          <span style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '24px', height: '24px', borderRadius: '50%',
            backgroundColor: toast.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white', fontSize: '14px', fontWeight: 'bold'
          }}>
            {toast.type === 'success' ? '\u2713' : '\u2715'}
          </span>
          <span style={{
            color: toast.type === 'success' ? '#166534' : '#dc2626',
            fontWeight: 500, fontSize: '14px'
          }}>
            {toast.message}
          </span>
          <button
            onClick={() => setToast({ ...toast, show: false })}
            style={{
              marginLeft: '8px', padding: '4px', backgroundColor: 'transparent',
              border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', lineHeight: 1
            }}
          >
            \u00d7
          </button>
        </div>
      )}

      {/* Info banner */}
      <div style={{
        padding: '16px 20px', marginBottom: '24px',
        backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
        borderRadius: '10px', display: 'flex', alignItems: 'flex-start', gap: '12px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <div>
          <div style={{ fontSize: '14px', color: '#1e40af', fontWeight: '500' }}>
            Mode de partage des ressources
          </div>
          <div style={{ fontSize: '13px', color: '#3b82f6', marginTop: '4px' }}>
            <strong>Partag&eacute; (organisation)</strong> : tous les &eacute;tablissements voient les m&ecirc;mes donn&eacute;es.
            <br />
            <strong>Isol&eacute; (&eacute;tablissement)</strong> : chaque &eacute;tablissement a ses propres donn&eacute;es.
          </div>
        </div>
      </div>

      {/* Resource cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {resources.map(([key, resource]) => {
          const isUpdating = updating === key;
          const icon = RESOURCE_ICONS[key] || '\u2699';
          const warning = SCOPE_WARNINGS[key]?.[resource.scope === 'organization' ? 'establishment' : 'organization'];

          return (
            <div key={key} style={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: isPhone ? 16 : 24,
              opacity: isUpdating ? 0.7 : 1,
              transition: 'opacity 0.2s'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                {/* Icon */}
                <div style={{
                  width: '44px', height: '44px', borderRadius: '10px',
                  backgroundColor: '#f1f5f9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '22px', flexShrink: 0
                }}>
                  {icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>
                    {resource.label}
                  </div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                    {resource.description}
                  </div>

                  {/* Radio buttons (lecture seule si !canManage) */}
                  <div style={{ display: 'flex', flexDirection: isPhone ? 'column' : 'row', gap: isPhone ? '12px' : '24px', marginTop: '16px' }}>
                    {/* Option Organisation */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 16px', borderRadius: '8px',
                      border: resource.scope === 'organization' ? '2px solid #3b82f6' : '2px solid #e2e8f0',
                      backgroundColor: resource.scope === 'organization' ? '#eff6ff' : 'white',
                      cursor: (isUpdating || !canManage) ? 'not-allowed' : 'pointer',
                      opacity: !canManage ? 0.7 : 1,
                      transition: 'all 0.15s', flex: 1
                    }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        border: resource.scope === 'organization' ? '2px solid #3b82f6' : '2px solid #cbd5e1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        {resource.scope === 'organization' && (
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                        )}
                      </div>
                      <div>
                        <div style={{
                          fontSize: '14px', fontWeight: resource.scope === 'organization' ? '600' : '400',
                          color: resource.scope === 'organization' ? '#1e40af' : '#475569'
                        }}>
                          Partag&eacute; (organisation)
                        </div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                          Commun &agrave; tous les &eacute;tablissements
                        </div>
                      </div>
                      <input
                        type="radio"
                        name={`scope-${key}`}
                        checked={resource.scope === 'organization'}
                        onChange={() => handleScopeChange(key, 'organization')}
                        disabled={isUpdating || !canManage}
                        style={{ display: 'none' }}
                      />
                    </label>

                    {/* Option Establishment */}
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 16px', borderRadius: '8px',
                      border: resource.scope === 'establishment' ? '2px solid #8b5cf6' : '2px solid #e2e8f0',
                      backgroundColor: resource.scope === 'establishment' ? '#f5f3ff' : 'white',
                      cursor: (isUpdating || !canManage) ? 'not-allowed' : 'pointer',
                      opacity: !canManage ? 0.7 : 1,
                      transition: 'all 0.15s', flex: 1
                    }}>
                      <div style={{
                        width: '20px', height: '20px', borderRadius: '50%',
                        border: resource.scope === 'establishment' ? '2px solid #8b5cf6' : '2px solid #cbd5e1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                      }}>
                        {resource.scope === 'establishment' && (
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#8b5cf6' }} />
                        )}
                      </div>
                      <div>
                        <div style={{
                          fontSize: '14px', fontWeight: resource.scope === 'establishment' ? '600' : '400',
                          color: resource.scope === 'establishment' ? '#6d28d9' : '#475569'
                        }}>
                          Isol&eacute; (&eacute;tablissement)
                        </div>
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                          Propre &agrave; chaque &eacute;tablissement
                        </div>
                      </div>
                      <input
                        type="radio"
                        name={`scope-${key}`}
                        checked={resource.scope === 'establishment'}
                        onChange={() => handleScopeChange(key, 'establishment')}
                        disabled={isUpdating || !canManage}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>

                  {/* Warning */}
                  {warning && (
                    <div style={{
                      marginTop: '12px', padding: '10px 14px',
                      backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                      borderRadius: '8px', fontSize: '13px', color: '#92400e',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" style={{ flexShrink: 0 }}>
                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                      </svg>
                      {warning}
                    </div>
                  )}
                </div>
              </div>

              {/* Updating indicator */}
              {isUpdating && (
                <div style={{
                  marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px',
                  fontSize: '13px', color: '#3b82f6'
                }}>
                  <div style={{
                    width: '14px', height: '14px',
                    border: '2px solid #bfdbfe', borderTop: '2px solid #3b82f6',
                    borderRadius: '50%', animation: 'spin 0.8s linear infinite'
                  }} />
                  Mise a jour en cours...
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default SharingSettingsPage;
